import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import {
  ItemToResolve, SwapOrReturnTypesEnum, SwapOrReturnTypeIds,
  CountTypeEnum,
  ReturnBatchToResolve,
} from '../../SwapOrReturnTypes'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as S from './ModalCountProductQuantityReturn.styles'
import { useEffect, useState } from 'react'
import { handleOnKeyDown } from 'src/utils/Form'
import { useDispatch } from 'react-redux'
import { actions } from '../../SwapOrReturnState'
import ModalAlert from 'src/components/ModalAlert'
import { Box, FormHelperText, Typography } from '@mui/material'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import { toast } from 'react-toastify'
import { InfoSupplier } from 'src/features/receivement/ReceivementTypes'
import ModalCountBatchesToReturn from './ModalCountBatchesToReturn'

interface ModalCountProductQuantityProps {
  show: boolean
  swapOrReturnTypeId: number
  infoSupplier: string | null
  product: ItemToResolve
  onConfirm: () => void
  onClose: () => void
}

interface ModalFormProps {
  productBarcode: string
  batches: ReturnBatchToResolve[]
}

export const ModalCountProductQuantityReturn = ({
  show,
  swapOrReturnTypeId,
  infoSupplier,
  product,
  onConfirm,
  onClose,
}: ModalCountProductQuantityProps) => {
  const {
    control, register, getValues, reset, setError, clearErrors, formState: { errors },
  } = useForm<ModalFormProps>({ defaultValues: { batches: product.returnBatches } })

  const { fields } = useFieldArray({
    control,
    name: 'batches',
  })

  const dispatch = useDispatch()

  const [showModalCountBatchesToReturn, setShowModalCountBatchesToReturn] = useState<boolean>(false)
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<number | null>(null)
  const [batchesToResolve, setBatchesToResolve] = useState<ReturnBatchToResolve[]>(product.returnBatches)

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false)
  const [alertMessage, setAlertMessage] = useState<JSX.Element>(<></>)

  const [validatedProductBarcode, setValidatedProductBarcode] = useState<boolean>(false)

  const [showProductNotFoundModal, setShowProductNotFoundModal] = useState<boolean>(false)

  const [preventDoubleClick, setPreventDoubleClick] = useState<boolean>(false)

  const totalCountedReturnQuantity = batchesToResolve.reduce((acc, batch) => acc + (batch.countedQuantity || 0), 0)

  const columns = [
    {
      name: 'Lote',
      width: '12%',
    },
    {
      name: 'Nº Lote',
      width: '12%',
    },
    {
      name: 'Validade',
      width: '14%',
    },
    {
      name: `Qtd. à retornar (${product.measureUnit})`,
      width: '21%',
    },
    {
      name: `Qtd. contada (${product.measureUnit})`,
      width: '21%',
    },
    {
      name: 'Contar',
      width: '18%',
    },
  ]

  const validateQuantityReturn = () => {
    const errorMessage = {
      simple: '',
      complete: '',
    }

    if (swapOrReturnTypeId === SwapOrReturnTypeIds.RETURN) {
      if (infoSupplier === InfoSupplier.ACCEPT_NFD) {
        if (totalCountedReturnQuantity !== 0 && product.returnQuantity !== totalCountedReturnQuantity) {
          errorMessage.simple = `Fornecedor ${infoSupplier}`
          errorMessage.complete = `Fornecedor ${infoSupplier} - a quantidade contada a retornar deve ser 0 ou ${product.returnQuantity}`
        }
      } else if (infoSupplier === InfoSupplier.RETURN_BY_BOXES) {
        if (totalCountedReturnQuantity !== 0 && totalCountedReturnQuantity % product.packQuantity !== 0) {
          errorMessage.simple = `Fornecedor ${infoSupplier}`
          errorMessage.complete = `Fornecedor ${infoSupplier} - a quantidade contada a retornar deve ser 0 ou múltipla de ${product.packQuantity}`
        }
      }
      return errorMessage
    }
    return errorMessage
  }

  const onSubmit = async () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    clearErrors()

    if (!validatedProductBarcode) {
      setError('productBarcode', { message: 'Código de barras não validado!' })
      setPreventDoubleClick(false)
      return
    }

    let hasBatchErrors = false
    batchesToResolve.forEach((batch, index) => {
      if (batch.countedQuantity !== undefined && batch.countedQuantity !== null) {
        if (batch.countedQuantity < 0) {
          setError(`batches.${index}.countedQuantity`, { message: 'Não pode ser negativo' })
          hasBatchErrors = true
        } else if (batch.countedQuantity > batch.quantity) {
          setError(`batches.${index}.countedQuantity`, { message: 'Qtd. maior que o lote' })
          hasBatchErrors = true
        } else if (!product.isKg && !new RegExp('^[0-9]*$').test(batch.countedQuantity.toString())) {
          setError(`batches.${index}.countedQuantity`, { message: 'Deve ser inteiro' })
          hasBatchErrors = true
        }
      }
    })

    if (hasBatchErrors) {
      toast.error('Corrija os campos com erro!')
      setPreventDoubleClick(false)
      return
    }

    const totalQuantity = batchesToResolve.reduce((acc, batch) => acc + (Number(batch.countedQuantity) || 0), 0)

    if (swapOrReturnTypeId === SwapOrReturnTypeIds.SWAP && product.receiveQuantityCounted && product.receiveBatches) {
      const totalCountedReceiveQuantity = product.receiveBatches.reduce((acc, batch) => acc + (batch.quantity || 0), 0)
      if (totalCountedReceiveQuantity < totalQuantity) {
        setAlertMessage(
          <S.AlertMessageBox>
            <Typography variant='body1'>
              {`A quantidade total contada (${totalQuantity}) para retornar é maior que a quantidade a receber (${totalCountedReceiveQuantity}).`}
            </Typography>
            <Typography variant='body1'>
              A quantidade a receber deve ser maior ou igual a quantidade a devolver!
            </Typography>
          </S.AlertMessageBox>,
        )
        setPreventDoubleClick(false)
        setShowAlertModal(true)
        return
      }
    }
    if (product.returnQuantity < totalQuantity) {
      setAlertMessage(
        <S.AlertMessageBox>
          <Typography variant='body1'>
            {`A quantidade total contada (${totalQuantity}) é maior que a quantidade a retornar (${product.returnQuantity}).`}
          </Typography>
          <Typography variant='body1'>
            Remova a quantidade excedente!
          </Typography>
        </S.AlertMessageBox>,
      )
      setPreventDoubleClick(false)
      setShowAlertModal(true)
      return
    }

    const validateQuantityReturnError = validateQuantityReturn().complete
    if (validateQuantityReturnError) {
      setAlertMessage(
        <S.AlertMessageBox>
          <Typography variant='body1'>
            {validateQuantityReturnError}
          </Typography>
        </S.AlertMessageBox>,
      )
      setPreventDoubleClick(false)
      setShowAlertModal(true)
      return
    }

    const onSuccess = () => {
      setPreventDoubleClick(false)
      onConfirm()
    }

    dispatch(actions.countBatchesToReturn({
      idItem: product.idItem,
      batches: batchesToResolve.map((batch) => ({
        idRequestItemBatch: batch.idRequestItemBatch,
        quantity: batch.countedQuantity || 0,
        expirationBatches: batch.expirationBatches,
      })),
      onSuccess,
      onError: () => setPreventDoubleClick(false),
    }))
  }

  const validateProductBarcode = () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    clearErrors('productBarcode')

    const productBarcode = getValues('productBarcode')

    if (!productBarcode) {
      setError('productBarcode', { message: 'Campo obrigatório' })
      setPreventDoubleClick(false)
      return
    }

    const onSuccess = () => {
      setValidatedProductBarcode(true)
      setPreventDoubleClick(false)
    }

    const onError = (message: string) => {
      setError('productBarcode', { message })
      setPreventDoubleClick(false)
    }

    dispatch(actions.validateSwapOrReturnToResolve({
      itemId: product.idItem,
      productBarcode,
      registerBarcode: false,
      onSuccess,
      onError,
    }))
  }

  const productNotFound = () => {
    if (preventDoubleClick) return

    setPreventDoubleClick(true)

    if (!showProductNotFoundModal) {
      setShowProductNotFoundModal(true)
      setPreventDoubleClick(false)
      return
    }

    const onSuccess = () => {
      setShowProductNotFoundModal(false)
      onClose()
    }

    dispatch(actions.swapOrReturnToResolveItemNotFound({
      itemId: product.idItem,
      onSuccess,
    }))

    setPreventDoubleClick(false)
  }

  const internalOnClose = () => {
    if (preventDoubleClick) return
    onClose()
  }

  const countBatch = (index: number) => {
    setSelectedBatchIndex(index)
    setShowModalCountBatchesToReturn(true)
  }

  useEffect(() => {
    reset()
    reset({ batches: product.returnBatches })
    clearErrors()
    setValidatedProductBarcode(false)
    setBatchesToResolve(product.returnBatches)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  useEffect(() => {
    reset({ batches: batchesToResolve })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalCountBatchesToReturn])

  return (
    <>
      <ConfirmOrRejectModal
        show={show}
        title={`Contagem de produto a ${CountTypeEnum.RETURN.toUpperCase()}`}
        onConfirm={onSubmit}
        onReject={internalOnClose}
        onClose={internalOnClose}
        customizableDescription={(
          <S.Form>
            <S.ProductInfoBox>
              {product.productDescription}
            </S.ProductInfoBox>
            <S.CustomTextField
              inputProps={{
                ...register('productBarcode'),
                'data-testid': 'product-barcode-input-test-id',
              }}
              id='productBarcode'
              name='productBarcode'
              error={!!errors.productBarcode}
              helperText={errors.productBarcode?.message}
              testId='product-barcode-input-test-id'
              label='Bipe o código do produto'
              type='text'
              autoComplete='off'
              disabled={validatedProductBarcode || preventDoubleClick}
              onKeyDown={(e) => handleOnKeyDown(e, '', validateProductBarcode)}
              fullWidth
              autoFocus
            />
            <S.CustomButton
              title='Não encontrei o produto'
              bgColor='orange2'
              hoverColor='orange'
              letterColor='white'
              data-testid='product-not-found-button-test-id'
              fullWidth
              onClick={productNotFound}
              hidden={validatedProductBarcode}
            />
            {validatedProductBarcode && (
              <>
                <S.ContainerRows data-testid='table-count-return-test-id'>
                  <S.Row
                    style={{
                      position: 'sticky', top: 0, background: '#EFEFEF', zIndex: 1,
                    }}
                    data-testid='table-header-test-id'
                  >
                    {columns.map((column, index) => (
                      <S.Column key={index} minWidth={column.width}>
                        <Typography sx={{ fontWeight: 'bold' }}>{column.name}</Typography>
                      </S.Column>
                    ))}
                  </S.Row>
                  {fields.map((batch, index) => (
                    <S.Row
                      key={batch.idBatch}
                      data-testid='table-row-test-id'
                    >
                      <S.Column
                        minWidth={columns[0].width}
                        data-testid='batch-column-test-id'
                      >
                        <Typography>{batch.idBatch}</Typography>
                      </S.Column>
                      <S.Column
                        minWidth={columns[1].width}
                        data-testid='batch-number-column-test-id'
                      >
                        <Typography>{batch.batchNumber || '-'}</Typography>
                      </S.Column>
                      <S.Column
                        minWidth={columns[2].width}
                        data-testid='due-date-column-test-id'
                      >
                        <Typography>{formatDateReplaceHour(new Date(batch.dueDate))}</Typography>
                      </S.Column>
                      <S.Column
                        minWidth={columns[3].width}
                        data-testid='quantity-column-test-id'
                      >
                        <Typography>{batch.quantity}</Typography>
                      </S.Column>
                      <S.Column
                        minWidth={columns[4].width}
                        data-testid='counted-quantity-column-test-id'
                      >
                        <Controller
                          name={`batches.${index}.countedQuantity`}
                          control={control}
                          defaultValue={batch.countedQuantity}
                          render={() => (
                            <>
                              <Typography>{batch.countedQuantity || 0}</Typography>
                              {errors?.batches && errors.batches[index] && errors.batches[index].countedQuantity ? (
                                <FormHelperText error>
                                  {errors?.batches[index]?.countedQuantity?.message ?? ''}
                                </FormHelperText>
                              ) : ''}
                            </>
                          )}
                        />
                      </S.Column>
                      <S.Column
                        minWidth={columns[5].width}
                        data-testid='count-button-column-test-id'
                      >
                        <S.CustomButton
                          title='Contar'
                          bgColor='blue'
                          hoverColor='darkBlueHover'
                          letterColor='white'
                          data-testid='count-batches-button-test-id'
                          fullWidth
                          onClick={() => countBatch(index)}
                          disabled={!validatedProductBarcode || preventDoubleClick}
                        />
                      </S.Column>
                    </S.Row>
                  ))}
                </S.ContainerRows>
                <S.QuantityInfoBox>
                  <S.QuantityInfoText>
                    Caixaria: {product.packQuantity}
                  </S.QuantityInfoText>
                  <S.QuantityInfoText>
                    Quantidade total à retornar: {totalCountedReturnQuantity} {product.measureUnit}
                  </S.QuantityInfoText>
                </S.QuantityInfoBox>
                <S.QuantityError>
                  {validateQuantityReturn().simple}
                </S.QuantityError>
              </>
            )}
          </S.Form>
        )}
        dataTestId='count-product-return-quantity-modal-test-id'
        confirmButtonText='Confirmar'
        rejectButtonText='Cancelar'
        size='lg'
      />
      <ModalAlert
        show={showAlertModal}
        title={`${SwapOrReturnTypesEnum[SwapOrReturnTypeIds[swapOrReturnTypeId]].toUpperCase()} - ${CountTypeEnum.RETURN.toUpperCase()}`}
        customizableBody={(
          <S.BodyModalAlert data-testid='modal-alert-body-test-id'>
            {alertMessage}
          </S.BodyModalAlert>
        )}
        action={() => setShowAlertModal(false)}
        centered
        testId='modal-alert-test-id'
      />
      <ConfirmOrRejectModal
        show={showProductNotFoundModal}
        title='Produto não encontrado'
        customizableDescription={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <span>Ao confirmar que o produto não foi encontrado, o produto será cancelado.</span>
            <span>Deseja confirmar que o produto não foi encontrado?</span>
          </Box>
        )}
        onConfirm={productNotFound}
        onReject={() => setShowProductNotFoundModal(false)}
        onClose={() => setShowProductNotFoundModal(false)}
        confirmButtonText='Confirmar'
        rejectButtonText='Cancelar'
        dataTestId='product-not-found-modal-test-id'
      />
      {selectedBatchIndex !== null && (
        <ModalCountBatchesToReturn
          show={showModalCountBatchesToReturn}
          product={product}
          batchToResolve={batchesToResolve[selectedBatchIndex]}
          batchesToResolve={batchesToResolve}
          setBatchesToResolve={setBatchesToResolve}
          onClose={() => setShowModalCountBatchesToReturn(false)}
        />
      )}
    </>
  )
}
